import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { Button, Container, Grid, TextField } from "@mui/material";
import toast from "react-hot-toast";

function PaperSubmissionForm({ conferenceId }) {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [country, setCountry] = useState(null);
  const [title, setTitle] = useState("");
  const [abstract, setAbstract] = useState("");
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [countriesOptions, setCountriesOptions] = useState([]);

  // 🔥 DEADLINE STATES
  const [deadline, setDeadline] = useState(null);
  const [deadlineDate, setDeadlineDate] = useState(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deadlineLoading, setDeadlineLoading] = useState(true);

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const CountryAPI = "https://eventscons.com/eventbackendnew/api/countries";
  const DeadlineAPI = "https://eventscons.com/eventbackendnew/api/closing/date";
  const getToken = () => localStorage.getItem("token");

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await axios.get(CountryAPI);
      const formatted = response.data?.data?.map((c) => {
        const iso2 = (c.iso2 || c.sortname || "").toLowerCase();
        return {
          value: c.name,
          label: (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={`https://flagcdn.com/w40/${iso2}.png`}
                alt={c.name}
                style={{ width: 22, marginRight: 8, borderRadius: 4 }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              {c.name}
            </div>
          ),
          raw: c,
        };
      });
      setCountriesOptions(formatted);
    } catch (err) {
      toast.error("Failed to load countries");
    }
  };

  // 🔥 FIXED: Abstract deadline for THIS conference (NO TIME)
  const fetchDeadline = async () => {
    try {
      setDeadlineLoading(true);
      const response = await axios.get(DeadlineAPI);
      const arr = response?.data?.data ?? [];
      
      // 🔥 SPECIFIC: type="abstract" AND this conference_id
      const abstractDeadlines = arr.filter(
        (item) => 
          String(item.type || "").toLowerCase() === "abstract" &&
          Number(item.conference_id) === Number(conferenceId)
      );

      if (abstractDeadlines.length === 0) {
        setDeadline("No abstract deadline found for this conference");
        setDeadlineDate(null);
        setIsDeadlinePassed(false);
        return;
      }

      // Get latest abstract deadline for this conference
      const latest = abstractDeadlines.reduce((max, item) =>
        new Date(item.closing_date) > new Date(max.closing_date) ? item : max
      );

      const dateObj = new Date(latest.closing_date);
      setDeadlineDate(dateObj);

      // 🔥 PROPER DATE COMPARISON (date only)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const closingDate = new Date(latest.closing_date);
      closingDate.setHours(0, 0, 0, 0);

      setIsDeadlinePassed(closingDate < today);

      // 🔥 NO TIME - JUST DATE
      setDeadline(dateObj.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      }));
      
    } catch (err) {
      console.error("Deadline fetch error:", err);
      setDeadline("Error fetching abstract deadline");
      setIsDeadlinePassed(false);
    } finally {
      setDeadlineLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    if (conferenceId) {
      fetchDeadline();
    }
  }, [conferenceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDeadlinePassed) {
      toast.error("❌ Abstract submission closed for this conference.");
      return;
    }

    if (!file) {
      toast.error("Please upload a PDF/DOC/DOCX file.");
      return;
    }

    if (!title || !abstract || !name || !email || !password) {
      toast.error("Please fill all required fields.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("conference_id", Number(conferenceId));
    formData.append("title", title);
    formData.append("abstract", abstract);
    formData.append("status", "under review");
    formData.append("submitted_at", new Date().toISOString().split("T")[0]);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    formData.append("phone_number", phone);
    formData.append("whatsapp_number", whatsApp);
    formData.append("nationality", country?.value || "india");
    formData.append("country_of_residence", country?.value || "india");
    formData.append("is_visa_payment_required", "1");
    formData.append("file_path", file);

    console.log("Submitting FormData entries:");
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    try {
      const token = getToken();
      const response = await axios.post(
        "https://eventscons.com/eventbackendnew/api/abstract",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      console.log("Success response:", response.data);
      toast.success("Your abstract has been successfully submitted!");
      
      setName("");
      setEmail("");
      setPhone("");
      setWhatsApp("");
      setCountry(null);
      setTitle("");
      setAbstract("");
      setFile(null);
      setPassword("");
      
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
      
    } catch (err) {
      console.error("Submit Error (Full):", err);
      console.error("Response Status:", err.response?.status);
      console.error("Response Data:", err.response?.data);
      
      let errorMessage = "Submission failed.";
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      else if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat();
        errorMessage = validationErrors.join(", ");
      }
      else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      else if (typeof err.response?.data === 'object') {
        errorMessage = JSON.stringify(err.response.data).replace(/["{}]/g, '');
      }
      else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* DEADLINE BOX - NO TIME */}
      <div
        style={{
          background: "#FFF4E5",
          borderLeft: "6px solid rgb(155, 19, 33)",
          padding: "18px",
          borderRadius: "10px",
          marginBottom: "25px",
          marginTop: "20px",
        }}
      >
        <strong style={{ fontSize: "20px" }}>
          📢 Abstract Submission Deadline:
        </strong>
        <div
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "rgb(155, 19, 33)",
          }}
        >
          {deadlineLoading 
            ? "Loading deadline..." 
            : deadline || "No deadline found"
          }
        </div>

        {isDeadlinePassed && !deadlineLoading && (
          <div style={{ 
            color: "#dc2626", 
            fontWeight: "bold", 
            marginTop: 12,
            backgroundColor: "#fee2e2",
            padding: "12px 18px",
            borderRadius: "8px",
            border: "1px solid #fecaca",
            fontSize: "16px"
          }}>
            ❌ Submission Closed — Deadline Passed for Conference #{conferenceId}
          </div>
        )}
      </div>

      <h3 style={{ marginBottom: "20px", fontWeight: 600 }}>
        Conference ID: {conferenceId}
      </h3>

      {/* FORM CONDITION */}
      {!isDeadlinePassed && !deadlineLoading ? (
        <form onSubmit={handleSubmit} className="register-form">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Paper Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                value={whatsApp}
                onChange={(e) => setWhatsApp(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Select
                options={countriesOptions}
                value={country}
                onChange={setCountry}
                placeholder="Select Country"
                isSearchable
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Abstract"
                value={abstract}
                onChange={(e) => setAbstract(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
               
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                accept=".pdf,.doc,.docx"
                inputProps={{ multiple: false }}
                required
                helperText={file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)` : "Upload PDF, DOC or DOCX only (Max 10MB)"}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent="center" style={{ marginTop: 24 }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="error"
              disabled={loading || isDeadlinePassed || deadlineLoading}
              size="large"
              sx={{ minWidth: 200, py: 1.5 }}
              style={{marginBottom:'30px'}}
            >
              {loading ? "Uploading..." : "Submit Abstract"}
            </Button>
          </Grid>
        </form>
      ) : isDeadlinePassed ? (
        <div style={{ 
          textAlign: "center", 
          marginTop: "40px", 
          padding: "40px", 
          background: "#fee2e2", 
          borderRadius: "16px",
          border: "3px solid #dc2626"
        }}>
          <h2 style={{ color: "#dc2626", marginBottom: 20 }}>
            ⏰ Abstract Submission Closed
          </h2>
          <p style={{ color: "#b91c1c", fontSize: "18px", lineHeight: 1.6 }}>
            Deadline has passed for Conference #{conferenceId}. 
            Please contact organizers for late submission requests.
          </p>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h3>Loading deadline information...</h3>
        </div>
      )}
    </Container>
  );
}

export default PaperSubmissionForm;
